import { useCallback, useEffect, useState } from 'react';
import StyledImage from '@apps/components/Image';
import { LightboxCarousel } from '@apps/components/Lightbox';
import { Box, Skeleton, Typography } from '@mui/material';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import { Slide } from 'yet-another-react-lightbox';

import { CarPhotosGalleryProps, GalleryPhoto } from '../props';

import ButtonRender from './ArrowButton';

export const CarPhotosGalleryView: React.FC<CarPhotosGalleryProps> = ({
  imageList = [],
  isLoading,
  carTitle,
}) => {
  // =================== STATE
  // const [imageList, setGallery] = useState<GalleryPhoto[]>(imageList || []);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [open, setOpen] = useState(false);

  // =================== EFFECTS

  //If imageList changes, reset index to 0 ( Eg Exterior to Interior)
  useEffect(() => {
    if (imageList) {
      setSelectedIndex(0);
    }
  }, [imageList]);

  // =================== HOOKS

  const { md: isMd, lg: isLg, xl: isXl } = useGetResponsive();

  const slides: Slide[] = imageList?.map((item, index) => {
    return {
      alt: `${carTitle} ${index + 1}`,
      src: item?.car_model_image_ori || '',
    };
  });
  // =================== EVENTS

  // Add selected Index to set the image to the index number
  const moveImageToRight = useCallback(() => {
    if (imageList) {
      setSelectedIndex((prevIndex) => (prevIndex + 1) % imageList?.length);
    }
  }, [imageList]);

  // Minus selected Index to set the image to the index number
  const moveImageToLeft = useCallback(() => {
    if (imageList) {
      setSelectedIndex(
        (prevIndex) => (prevIndex - 1 + imageList.length) % imageList.length
      );
    }
  }, [imageList]);

  // Handles image click and updates the current selected image to the clicked image and update the imageList
  const handleImageClick = (index: number) => {
    setSelectedIndex(index);
  };

  //Close the backdrop
  const onClose = () => {
    setOpen(false);
  };
  //Open the backdrop
  const onHandleOpen = () => {
    setOpen(true);
  };

  // ================== RENDER FUNCTIONS

  //Render Left Side of StyledImage
  const renderLeftSide = (image: GalleryPhoto) => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          cursor: 'pointer',
          position: 'relative',
          backgroundColor: '#F1F1F1',
        }}
      >
        {/* StyledImage and Button */}

        {imageList && image ? (
          <Box
            sx={{
              display: 'flex',
              height: '100%',
              width: '100%',
              aspectRatio: '3/2',
              alignItems: 'center',
            }}
          >
            <StyledImage
              src={
                open
                  ? imageList[0]?.car_model_image_ori
                  : `${
                      imageList[selectedIndex % imageList.length]
                        ?.car_model_image_ori
                    }`
              }
              alt={`${carTitle} 1`}
              key={0}
              width="0"
              height="0"
              loading="lazy"
              sizes={isMd ? '64vw' : '100vw'}
              priority={false}
              onClick={() => onHandleOpen()}
              imageStyle={{
                objectFit: 'contain',
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                backgroundColor: '#F1f1f1',
              }}
            />
            <Box
              sx={{
                display: 'flex',
                position: 'absolute',
                bottom: 0,
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                height: 'auto',
                background:
                  'linear-gradient(to top, rgba(0, 0, 0, 0.6) 20%, rgba(0, 0, 0, 0) 100%)',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              <Typography
                sx={{
                  color: 'white',
                  paddingX: '0.5rem',
                  paddingY: '0.25rem',
                  fontSize: '1rem',
                  fontWeight: '700',
                }}
              >{`${selectedIndex + 1}/${imageList.length}`}</Typography>
            </Box>
          </Box>
        ) : null}

        <ButtonRender
          size={isMd ? '2rem' : '2.5rem'}
          xPos={isMd ? '0.313rem' : '1rem'}
          leftFunction={moveImageToLeft}
          rightFunction={moveImageToRight}
          isTransparent={true}
        />
      </Box>
    );
  };

  //Render Right Side of StyledImage
  const renderRightSide = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'row',
            md: 'column',
          },
          justifyItems: 'space-between',
          alignItems: 'end',
          width: { md: '27%' },
          height: 'auto',
          paddingLeft: isMd ? 0 : 4,
          columnGap: isMd ? 1 : 4,
          rowGap: isMd ? 0 : 4,
        }}
      >
        {/* If StyledImage List exist */}
        {imageList
          ? // If image list has more than 4 images
            imageList.length > 5
            ? imageList.slice(1, 5).map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    position: 'relative',
                    width: '100%',
                    cursor: 'pointer',
                    justifyContent: 'space-between',
                    flex: 1,
                    // columnGap: 'auto',
                  }}
                >
                  {index === 3 ? ( //Image with View More
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        aspectRatio: { xs: '4/3', md: '2/1' },
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        cursor: 'pointer',
                        backgroundColor: '#F1F1F1',
                      }}
                      onClick={() => {
                        handleImageClick(index + 1);
                        onHandleOpen();
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          height: '100%',
                        }}
                      >
                        <StyledImage
                          src={image?.car_model_image_ori || ''}
                          alt={`${carTitle} 5`}
                          loading={'lazy'}
                          priority={false}
                          width="0"
                          height="0"
                          sizes={'24vw'}
                          draggable={false}
                          quality={80}
                          imageStyle={{
                            objectFit: 'contain',
                            width: '100%',
                            height: 'auto',
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0',
                            flexGrow: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              flexGrow: 1,
                              color: 'white',
                              fontWeight: '500',
                              fontSize: (() => {
                                // if (isSm) return '0.5rem';
                                if (isMd) return '0.75rem';
                                if (isLg) return '0.875rem';
                                if (isXl) return '1rem';
                                return '1rem';
                              })(),
                              textAlign: 'center',
                              textJustify: 'center',
                            }}
                          >
                            {`View more`}
                            <br />
                            {`${imageList.length - 4} photos`}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    //Image without View More
                    <Box
                      key={index}
                      onClick={() => {
                        handleImageClick(index + 1);
                        onHandleOpen();
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        aspectRatio: { xs: '4/3', md: '2/1' },
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        cursor: 'pointer',
                        backgroundColor: '#F1F1F1',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          height: '100%',
                        }}
                      >
                        <StyledImage
                          src={image?.car_model_image_ori || ''}
                          alt={`${carTitle} ${index + 2}`}
                          loading={'lazy'}
                          priority={false}
                          imageStyle={{
                            objectFit: 'contain',
                            width: '100%',
                            height: 'auto',
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              ))
            : imageList.slice(1, imageList.length).map(
                (
                  image,
                  index //Image is less than 5
                ) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      cursor: 'pointer',
                      aspectRatio: '2/1',
                      backgroundColor: '#F1F1F1',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        height: '100%',
                      }}
                      onClick={() => {
                        handleImageClick(index + 1);
                        onHandleOpen();
                      }}
                    >
                      <StyledImage
                        src={image?.car_model_image_ori || ''}
                        alt={`${carTitle}`}
                        width="0"
                        height="0"
                        sizes={'24vw'}
                        draggable={false}
                        quality={80}
                        style={{
                          objectFit: 'contain',
                          height: '100%',
                          width: '100%',
                        }}
                      />
                    </Box>
                  </Box>
                )
              )
          : null}
      </Box>
    );
  };

  // =================== MAIN RENDER
  return (
    <>
      {isLoading ? (
        <Skeleton
          variant="rounded"
          width="100%"
          height={isMd ? '12rem' : '31.875rem'}
          sx={{ mt: isMd ? '1.25rem' : '1.875rem' }}
        />
      ) : (
        <>
          <Box
            sx={{
              rowGap: 4,
              display: 'flex',
              position: 'relative',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'start',
              alignItems: 'space-between',
              pt: '0.625rem',
            }}
          >
            {/* Left */}
            {renderLeftSide(imageList[0])}
            {/* Right */}
            {renderRightSide()}
          </Box>
          {/* {open && (
            <ImagePopper
              selectedIndex={selectedIndex}
              imageList={imageList}
              moveLeft={moveImageToLeft}
              moveRight={moveImageToRight}
              handleClose={onHandleClose}
              handleImageClick={handleImageClick}
            />
          )} */}
          <LightboxCarousel
            slides={slides}
            index={selectedIndex}
            open={open}
            close={onClose}
          />
        </>
      )}
    </>
  );
};

// Export
export default CarPhotosGalleryView;
